/**
 * T2-1 (#3964 (itemId=3969) c7043 m.2) — a `dm.plan`'s `data.conflict_check`: the conflict and placement scan its
 * approval records. The `plan-conflict-check` gate (requires_field) reads presence only and `{}` is present, so this
 * schema is the shape contract the kernel checks at every dm.plan write; the slices cross rule is the api leaf's
 * (`dmPlanConflictCheckPayload.ts`). `dm plan conflicts` returns {@link dmPlanConflictCheckSkeletonSchema}: the scan
 * filled, the classification left to the planner.
 */
import { z } from 'zod';
import { DM_INBOX_KINDS } from './dmInbox.js';
import { DM_PLAN_SLICE_KEY_PATTERN, DM_PLAN_SLICES_MAX } from './dmPlanSlices.js';
export const DM_PLAN_CONFLICT_CHECK_FIELD = 'conflict_check';
/** `searched.tool` of a scan `dm.plan.conflicts` produced (its CLI spelling). */
export const DM_PLAN_CONFLICT_SCAN_TOOL = 'dm plan conflicts';
const DM_PLAN_CONFLICT_SCAN_TOOLS = [DM_PLAN_CONFLICT_SCAN_TOOL, 'manual'];
export const DM_PLAN_CONFLICT_SOURCES = ['item', 'spec', 'policy', 'decision', 'doc', 'question'];
/** A scan that did not read these is not a conflict check. */
export const DM_PLAN_CONFLICT_REQUIRED_SOURCES = ['item', 'spec', 'policy'];
/**
 * The scan's enumerating arms, each read to a cap; `searched.truncated` names the ones that hit it. `area` is an area's
 * subtree window, `area_concerns` its concerns members' (T1-3b c7230).
 */
export const DM_PLAN_CONFLICT_SCAN_ARMS = ['edges', 'siblings', 'plans', 'questions', 'body', 'area', 'area_concerns'];
const DM_PLAN_CONFLICT_RELATIONS = ['conflict', 'dependency', 'duplicate', 'aligned', 'none'];
export const DM_PLAN_CONFLICT_DECISIONS = ['none', 'supersedes', 'follows_up', 'merged_into', 'coexists'];
const DM_PLAN_CONFLICT_EDGE_TYPES = ['follows_up', 'relates', 'supersedes'];
/** The edge each decision records: required for follows_up / supersedes, optional `relates` for coexists, none else. */
const DECISION_EDGE = {
    none: null,
    supersedes: 'supersedes',
    follows_up: 'follows_up',
    merged_into: null,
    coexists: 'relates',
};
const EDGE_REQUIRED = new Set(['supersedes', 'follows_up']);
const DM_PLAN_CONFLICT_HOSTS = ['this', 'external'];
const DM_PLAN_CONFLICT_PARENT_KINDS = ['epic', 'wave', 'area', 'container'];
const DM_PLAN_CONFLICT_KEYS_MAX = 16;
export const DM_PLAN_CONFLICT_KEY_MAX_CHARS = 64;
export const DM_PLAN_CONFLICT_CANDIDATES_MAX = 100;
export const DM_PLAN_CONFLICT_AREAS_MAX = 8;
export const DM_PLAN_CONFLICT_TITLE_MAX = 160;
const DM_PLAN_CONFLICT_RATIONALE_MAX = 300;
/** A decision other than `none` names why in at least this many characters. */
export const DM_PLAN_CONFLICT_RATIONALE_MIN = 20;
export const DM_PLAN_CONFLICT_QUESTIONS_MAX = 100;
export const DM_PLAN_CONFLICT_SUPERSEDED_BY_MAX = 20;
const positiveId = z.number().int().positive();
const rationale = z.string().trim().min(1).max(DM_PLAN_CONFLICT_RATIONALE_MAX);
const keySchema = z
    .string()
    .trim()
    .min(1)
    .max(DM_PLAN_CONFLICT_KEY_MAX_CHARS)
    .regex(/[\p{L}\p{N}]/u, 'a search key holds a letter or a digit');
function addDuplicates(ctx, path, values, what) {
    const seen = new Set();
    const dup = new Set();
    for (const value of values)
        (seen.has(value) ? dup : seen).add(value);
    if (dup.size > 0)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: `${what} must be unique: ${[...dup].join(', ')}` });
}
const searchedSchema = z
    .object({
    keys: z.array(keySchema).min(1).max(DM_PLAN_CONFLICT_KEYS_MAX),
    sources: z.array(z.enum(DM_PLAN_CONFLICT_SOURCES)).min(1),
    scope: z
        .object({
        projectId: positiveId,
        areaIds: z.array(positiveId).max(DM_PLAN_CONFLICT_AREAS_MAX).optional(),
        /** The scan reads stale rows labelled; a fresh-only scan would hide the history it exists to find. */
        freshOnly: z.literal(false),
    })
        .strict(),
    at: z.string().datetime({ offset: true }),
    tool: z.enum(DM_PLAN_CONFLICT_SCAN_TOOLS),
    /** The arms read to their cap (a capped arm is not a full read); [] = every arm complete. */
    truncated: z.array(z.enum(DM_PLAN_CONFLICT_SCAN_ARMS)).optional(),
})
    .strict()
    .superRefine((searched, ctx) => {
    addDuplicates(ctx, ['keys'], searched.keys, 'searched.keys');
    addDuplicates(ctx, ['sources'], searched.sources, 'searched.sources');
    addDuplicates(ctx, ['truncated'], searched.truncated ?? [], 'searched.truncated');
    const missing = DM_PLAN_CONFLICT_REQUIRED_SOURCES.filter((source) => !searched.sources.includes(source));
    if (missing.length > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sources'], message: `searched.sources must include ${missing.join(', ')}` });
    }
});
const edgeSchema = z.object({ type: z.enum(DM_PLAN_CONFLICT_EDGE_TYPES), toItemId: positiveId }).strict();
/** The candidate as the scan found it: identity, state and the stale label (`isDmStaleRow`). */
const candidateFound = {
    itemId: positiveId,
    itemNo: positiveId,
    type: z.string().min(1).max(64),
    status: z.string().min(1).max(32),
    stale: z.boolean(),
    supersededBy: z.array(positiveId).max(DM_PLAN_CONFLICT_SUPERSEDED_BY_MAX),
    title: z.string().trim().min(1).max(DM_PLAN_CONFLICT_TITLE_MAX),
};
const candidateSchema = z
    .object({
    ...candidateFound,
    relation: z.enum(DM_PLAN_CONFLICT_RELATIONS),
    decision: z.enum(DM_PLAN_CONFLICT_DECISIONS),
    rationale: rationale.optional(),
    edge: edgeSchema.optional(),
})
    .strict()
    .superRefine((candidate, ctx) => {
    if (candidate.decision !== 'none' && (candidate.rationale?.length ?? 0) < DM_PLAN_CONFLICT_RATIONALE_MIN) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['rationale'],
            message: `decision ${candidate.decision} names its rationale (at least ${DM_PLAN_CONFLICT_RATIONALE_MIN} characters)`,
        });
    }
    const expected = DECISION_EDGE[candidate.decision];
    if (candidate.edge === undefined) {
        if (EDGE_REQUIRED.has(candidate.decision)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['edge'], message: `decision ${candidate.decision} records its ${expected} edge` });
        }
        return;
    }
    if (expected === null || candidate.edge.type !== expected) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['edge', 'type'],
            message: `decision ${candidate.decision} records ${expected === null ? 'no edge' : `a ${expected} edge`}`,
        });
    }
    if (candidate.edge.toItemId !== candidate.itemId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['edge', 'toItemId'], message: 'the edge points at the candidate (toItemId = itemId)' });
    }
});
const skeletonCandidateSchema = z.object(candidateFound).strict();
const openQuestionSchema = z.object({ questionId: positiveId, itemId: positiveId, kind: z.enum(DM_INBOX_KINDS) }).strict();
const placementSchema = z
    .object({
    parentId: positiveId,
    parentKind: z.enum(DM_PLAN_CONFLICT_PARENT_KINDS),
    /** dm.area ids the work concerns (T1 K1 topics). */
    topics: z.array(positiveId).max(DM_PLAN_CONFLICT_AREAS_MAX).optional(),
    rationale,
})
    .strict();
const slicePlacementSchema = z
    .object({
    slice_key: z.string().min(1).max(64).regex(DM_PLAN_SLICE_KEY_PATTERN, 'a slice key is a slug (^[a-z0-9][a-z0-9-]*$)'),
    host: z.enum(DM_PLAN_CONFLICT_HOSTS),
    parentId: positiveId.optional(),
    edges: z.array(edgeSchema).max(8).optional(),
})
    .strict()
    .superRefine((placement, ctx) => {
    if ((placement.host === 'external') !== (placement.parentId !== undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['parentId'],
            message: 'an external placement names its parentId; a host-this slice sits under the plan parent and names none',
        });
    }
});
function refineScan(value, ctx) {
    addDuplicates(ctx, ['candidates'], value.candidates.map((c) => c.itemId), 'candidate itemIds');
    addDuplicates(ctx, ['openQuestions'], value.openQuestions.map((q) => q.questionId), 'openQuestions questionIds');
}
export const dmPlanConflictCheckSchema = z
    .object({
    searched: searchedSchema,
    /** Empty is a finding ("searched, found nothing"), not an omission. */
    candidates: z.array(candidateSchema).max(DM_PLAN_CONFLICT_CANDIDATES_MAX),
    /** The open questions read before asking a new one (q266); empty is valid. */
    openQuestions: z.array(openQuestionSchema).max(DM_PLAN_CONFLICT_QUESTIONS_MAX),
    placement: placementSchema,
    placements: z.array(slicePlacementSchema).max(DM_PLAN_SLICES_MAX),
    checked_by: z.string().trim().min(1).max(128),
})
    .strict()
    .superRefine((check, ctx) => {
    refineScan(check, ctx);
    addDuplicates(ctx, ['placements'], check.placements.map((p) => p.slice_key), 'placement slice_keys');
});
/** `dm plan conflicts` output: the scan filled, no relation / decision / placement (the planner's judgement). */
export const dmPlanConflictCheckSkeletonSchema = z
    .object({
    searched: searchedSchema,
    candidates: z.array(skeletonCandidateSchema).max(DM_PLAN_CONFLICT_CANDIDATES_MAX),
    openQuestions: z.array(openQuestionSchema).max(DM_PLAN_CONFLICT_QUESTIONS_MAX),
})
    .strict()
    .superRefine(refineScan);
//# sourceMappingURL=dmPlanConflictCheck.js.map